import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from "@angular/core";
import { AcademicGrid } from "./s25.academic.grid";
import { FormField, form, hidden, max, min, readonly } from "@angular/forms/signals";
import { S25SelectComponent } from "../../standalone/s25.select.component";
import { AcademicGridsWarningIconComponent } from "./s25.academic.grids.warning.icon.component";
import { AcademicGridStore } from "./s25.academic.grid.store";

@Component({
    standalone: true,
    selector: "s25-ng-academic-grids-warnings-menu",
    template: `
        <h3>Configure Warnings</h3>
        <div class="issues">
            <div class="issue">
                <s25-ng-academic-grids-warning-icon [warningLevel]="form.instructorConflicts().value().action" />
                <label>Instructor Conflicts: </label>
                <s25-ng-select
                    [formField]="form.instructorConflicts.action"
                    [options]="getWarningLevels('instructorConflicts')"
                />
            </div>
            @if (!form.locationConflicts().hidden()) {
                <div class="issue">
                    <s25-ng-academic-grids-warning-icon [warningLevel]="form.locationConflicts().value().action" />
                    <label>Location Conflicts: </label>
                    <s25-ng-select
                        [formField]="form.locationConflicts.action"
                        [options]="getWarningLevels('locationConflicts')"
                    />
                </div>
            }
            @if (!form.resourceConflicts().hidden()) {
                <div class="issue">
                    <s25-ng-academic-grids-warning-icon [warningLevel]="form.resourceConflicts().value().action" />
                    <label>Resource Conflicts: </label>
                    <s25-ng-select
                        [formField]="form.resourceConflicts.action"
                        [options]="getWarningLevels('resourceConflicts')"
                    />
                </div>
            }
            <div class="issue locationSize">
                <s25-ng-academic-grids-warning-icon [warningLevel]="form.locationUndersized().value().action" />
                <label>Undersized Locations: </label>
                <s25-ng-select
                    [formField]="form.locationUndersized.action"
                    [options]="getWarningLevels('locationUndersized')"
                />
                <div class="details">
                    <span>When</span>
                    <s25-ng-select [formField]="form.locationUndersized.ratio" [options]="headCountTypes" />
                    <span>></span>
                    <span class="threshold">
                        <input
                            class="c-input"
                            type="number"
                            step="1"
                            [formField]="form.locationUndersized.threshold"
                        />%
                    </span>
                </div>
            </div>
            <div class="issue locationSize">
                <s25-ng-academic-grids-warning-icon [warningLevel]="form.locationOversized().value().action" />
                <label>Oversized Locations: </label>
                <s25-ng-select
                    [formField]="form.locationOversized.action"
                    [options]="getWarningLevels('locationOversized')"
                />
                <div class="details">
                    <span>When</span>
                    <s25-ng-select [formField]="form.locationOversized.ratio" [options]="headCountTypes" />
                    <span><</span>
                    <span class="threshold">
                        <input class="c-input" type="number" step="1" [formField]="form.locationOversized.threshold" />%
                    </span>
                </div>
            </div>
            <div class="issue">
                <s25-ng-academic-grids-warning-icon [warningLevel]="form.missingFeaturePrefs().value().action" />
                <label>Missing Feature Preferences: </label>
                <s25-ng-select
                    [formField]="form.missingFeaturePrefs.action"
                    [options]="getWarningLevels('missingFeaturePrefs')"
                />
            </div>
            <div class="issue">
                <s25-ng-academic-grids-warning-icon [warningLevel]="form.missingPartitionPrefs().value().action" />
                <label>Missing Partition Preferences: </label>
                <s25-ng-select
                    [formField]="form.missingPartitionPrefs.action"
                    [options]="getWarningLevels('missingPartitionPrefs')"
                />
            </div>
            <div class="issue">
                <s25-ng-academic-grids-warning-icon [warningLevel]="form.standardSchedules().value().action" />
                <label>Standard Schedules: </label>
                <s25-ng-select
                    [formField]="form.standardSchedules.action"
                    [options]="getWarningLevels('standardSchedules')"
                />
            </div>
        </div>
    `,
    styles: `
        h3 {
            text-align: center;
        }

        .issues {
            display: grid;
            grid-template-columns: auto 17ch 1fr;
            column-gap: 0.5rem;
            row-gap: 0.5rem;
        }

        .issue {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1/-1;
            align-items: center;
        }

        .details {
            grid-column: 2/-1;
        }

        .issue.locationSize .details {
            display: flex;
            column-gap: 0.5rem;
            align-items: center;
            padding-block-start: 0.25rem;
            justify-content: space-between;

            .threshold input {
                width: 3rem;
                margin-inline-end: 0.25rem;
                -moz-appearance: textfield;

                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
            }
        }

        ::ng-deep ngb-popover-window:has(s25-ng-academic-grids-warnings-menu) {
            --bs-popover-max-width: 350px !important;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25SelectComponent, FormField, AcademicGridsWarningIconComponent],
})
export class S25AcademicGridsWarningsMenuComponent {
    readonly store = inject(AcademicGridStore);

    readonly form = form(this.store.warningsConfig, (schemaPath) => {
        readonly(schemaPath.locationConflicts, () => this.store.saveType() === "individual");
        readonly(schemaPath.resourceConflicts, () => this.store.saveType() === "individual");
        hidden(
            schemaPath.locationConflicts,
            () =>
                this.store.saveType() === "individual" &&
                !this.store.canEditLocation() &&
                !this.store.canEditSchedule(),
        );
        hidden(
            schemaPath.resourceConflicts,
            () => this.store.saveType() === "individual" && !this.store.canEditSchedule(),
        );
        min(schemaPath.locationUndersized.threshold, () => 0);
        max(schemaPath.locationUndersized.threshold, () => 100);
        min(schemaPath.locationOversized.threshold, () => 0);
        max(schemaPath.locationOversized.threshold, () => 100);
    });

    protected readonly warningLevels = Object.values(AcademicGrid.warningSeverities).map((severity) => ({
        value: severity.id,
        ...severity,
    }));

    protected readonly headCountTypes = [
        { value: "RHC:CAP", label: "RHC:CAP" },
        { value: "EHC:CAP", label: "EHC:CAP" },
    ] as const satisfies { value: AcademicGrid.WarningHeadCountRatio; label: string }[];

    getWarningLevels(type: AcademicGrid.ItemIssueType) {
        const maxSeverity = this.store.warningsConfig()[type].maxSeverity;
        if (!maxSeverity) return this.warningLevels;
        const maxSeverityValue = AcademicGrid.warningSeverities[maxSeverity].severity;
        return this.warningLevels.filter(({ severity }) => severity <= maxSeverityValue);
    }
}
